import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";

import { Controls, ControlsButton } from "@/components/Controls";
import SearchBar from "@/components/SearchBar";
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import { EventsStatusEnum, SortDirection } from "@/utils/enums";
import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import { EventsProjectResource, SortState } from "@/types/resources";
import EventsTableRow from "./EventsTableRow";
import useDebounce from "@/hooks/useDebounce";
import { api } from "@/utils/api";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "created_at",
};

export enum Tabs {
  Sent = EventsStatusEnum.SENT,
  Draft = EventsStatusEnum.DRAFT,
}

type LocationState =
  | {
      tab?: Tabs;
    }
  | undefined;

const StyledTable = styled(Table)`
  margin-bottom: 50px;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    font-weight: 700;
    &:nth-child(1) {
      padding-left: 24px;
      width: calc(28% - 52px);
    }
    &:nth-child(2) {
      width: 16%;
    }
    &:nth-child(3) {
      width: 27%;
    }
    &:nth-child(4) {
      width: 13%;
    }
    &:nth-child(5) {
      width: 14%;
    }
    &:last-child {
      width: 52px;
      position: relative;
      padding-right: 16px;
    }
  }
`;

const EventsTable = () => {
  const [search, setSearch] = useState("");

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const { teamId, status } = useParams();

  const navigate = useNavigate();

  const getTabFromStatus = () => {
    switch (status) {
      case "sent":
        return Tabs.Sent;
      case "draft":
        return Tabs.Draft;
    }
  };

  useEffect(() => {
    status !== "sent" &&
      status !== "draft" &&
      navigate("/404", {
        replace: true,
      });
  }, [status, navigate]);

  const location = useLocation();

  const locationState = location.state as LocationState;

  const activeTab = locationState?.tab ?? getTabFromStatus();

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery<EventsProjectResource[]>(
    [
      "production_events_projects",
      {
        status,
        ...(debouncedSearch.length > 2 && {
          search: debouncedSearch,
        }),
        sort,
        teamId,
      },
    ],
    () =>
      api.get<never, EventsProjectResource[]>(
        "/v1/production/events-projects",
        {
          params: {
            ...(debouncedSearch.length > 2 && {
              "filter[search]": debouncedSearch,
            }),
            "filter[status]":
              status === "sent"
                ? EventsStatusEnum.SENT
                : [EventsStatusEnum.DRAFT, EventsStatusEnum.CANCELED],
            ...(sort.direction !== "DEFAULT" && {
              sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
            }),
            ...(teamId !== "all" && {
              "filter[team_id]": teamId,
            }),
          },
        }
      ),
    {
      staleTime: 1000 * 60,
    }
  );

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.Sent}
            as={Link}
            to={`/teams/${teamId}/production/events/sent`}
          >
            Sent
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Draft}
            as={Link}
            to={`/teams/${teamId}/production/events/draft`}
          >
            Draft
          </ControlsButton>
        </Controls>
      </TableHeader>
      <SearchBar value={search} setValue={setSearch} />
      <SectionHeading>
        {status === "sent" ? "approved" : "draft"} events
      </SectionHeading>
      <StyledTable $fixed>
        <thead>
          <tr>
            <Th>Project Name</Th>
            <Th>Compilation ID</Th>
            <Th>Channel Owner</Th>
            <Th>Status</Th>
            <SortableTh
              property="created_at"
              title="Created Date"
              setSort={setSort}
              sort={sort}
            />
            <Th>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            isLoading={isLoading}
            colSpan={6}
            isError={isError}
            hasData={!!projects?.length}
          >
            {projects?.map((project, index) => (
              <EventsTableRow key={index} project={project} />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
    </>
  );
};

export default EventsTable;
